import { useDisclosure } from "@chakra-ui/hooks";
import AtriumApi from "api";
import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import AlertPane from "components/alert";
import Dialog from "components/dialog";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import PenEditIcon from "components/icons/PenEditIcon";
import PlusIcon from "components/icons/PlusIcon";
import { useDarkmode } from "hooks/useTheme";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DeleteIcon from "components/icons/DeleteIcon";
// import AddStaff from "./add-staff";
import DeleteStaff from "./delete";
import ConfirmationDialog from "./confirmation-dialog";
import toast from "react-hot-toast";
import ManageStaffDrawer from "./manage-staff";
import Loader from "components/loader/Loader";
import Avatar from "components/avatar";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useAuth } from "hooks/useAuth";

const OrganizationSettings = () => {
  const [church, setChurch] = useState({});
  const [room, setRoom] = useState({
    name: "",
    capacity: "",
    grade: "",
  });
  const [rooms, setRooms] = useState([]);

  const params = useParams();
  const { darkmode } = useDarkmode();
  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: !!params.id,
    onClose: () => {
      // navigate("/admin/settings");
    },
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSavingRoom, setIsSavingRoom] = useState(false);

  const qrDlg = useDisclosure();

  const [errors, setErrors] = useState([]);
  const [notice, setNotice] = useState("");

  const [roomErrors, setRoomErrors] = useState([]);

  const [staffList, setStaffList] = useState([]);
  const [staffDetails, setStaffDetails] = useState({});

  const [staffListLoading, setStaffListLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    message: "",
    onConfirm: () => {},
  });

  const { user } = useAuth();
  const [timezone, setTimezone] = useState("");
  const [isTimeZoneLoading, setIsTimeZoneLoading] = useState(false);
  const [updateTimeLoading, setUpdateTimeLoading] = useState(false);

  const id = user?.id;

  const getChurchDetails = async () => {
    setLoading(true);
    AtriumApi.get("/v1/church", { params: { "with-rooms": true } }).then(
      (resp) => {
        setChurch(resp?.data?.church);
        setRooms(resp?.data?.rooms);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    getChurchDetails();
  }, []);

  const onSaveChurch = async (values) => {
    const formElement = document.querySelector("form");
    formElement.classList.add("animate-pulse");

    const submitButton = formElement.querySelector('button[type="submit"]');
    submitButton.setAttribute("disabled", "");

    const {
      lat,
      lng,
      dateFormat,
      timezone,
      createdAt,
      updatedAt,
      zip,
      ...otherChurchData
    } = church; // <- this is where the "extra" data comes from

    // Combine Formik values + external church data
    const details = {
      ...otherChurchData, // comes from `church` state
      ...values, // comes from Formik
      zipCode: church?.zipCode || church?.zip, // zip rename fallback
    };

    try {
      const response = await AtriumApi.put("/v1/church", { church: details });

      if (response?.status === 422) {
        setErrors(response?.data.errors);
      } else if (response?.status === 200) {
        setNotice("Your changes are saved successfully");
        setChurch(response?.data?.church);
        getChurchDetails();
      } else {
        throw new Error("Unexpected response status: " + response?.status);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      formElement.classList.remove("animate-pulse");
      submitButton.removeAttribute("disabled");
    }
  };

  // const onSavePreferences = (e) => {
  //   e.preventDefault();
  //   setPreferencesNotice("");
  //   e.target.classList.add("animate-pulse");
  //   const submitButton = e.target.querySelector('button[type="submit"]');
  //   submitButton.setAttribute("disabled", "");
  //   const { dateFormat, timezone } = church;
  //   AtriumApi.put("/v1/church", { church: { dateFormat, timezone } }).then(
  //     (response) => {
  //       e.target.classList.remove("animate-pulse");
  //       submitButton.removeAttribute("disabled");
  //       if (response.status === 200) {
  //         setPreferencesNotice("Your changes are saved successfully");
  //         setChurch(response.data.church);
  //       } else {
  //         throw new Error("unexpected response");
  //       }
  //     }
  //   );
  // };

  const onSaveRoom = (values) => {
    setIsSavingRoom(true);

    const formElement = document.querySelector("form");
    formElement.classList.add("animate-pulse");

    const submitButton = formElement.querySelector('button[type="submit"]');
    submitButton.setAttribute("disabled", "");
    let api, url;
    if (room.id) {
      api = AtriumApi.put;
      url = "/v1/rooms/" + room.id;
    } else {
      api = AtriumApi.post;
      url = "/v1/rooms";
    }

    const payload = room.id ? { room: values } : { ...values };

    api(url, { ...payload })
      .then((response) => {
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");
        if (response.status === 422) {
          setRoomErrors(response.data.error);
          toast.error(response.data.error);
        } else if (response.status === 200) {
          if (room.id) {
            const collection = [...rooms];
            const index = collection.findIndex((r) => r.id === room.id);
            collection[index] = response.data;
            setRooms(collection);
          } else {
            setRooms([...rooms, response.data]);
          }
          onClose();
        } else {
          throw new Error("unexpected response");
        }
      })
      .catch((error) => {
        console.log("error", error);

        toast.error(error?.data?.message || "Something went wrong");
      })
      .finally(() => {
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");
        setIsSavingRoom(false);
      });
  };

  const newRoom = () => {
    setRoomErrors([]);
    setRoom({
      name: "",
      capacity: "",
      grade: "",
    });
    onOpen();
  };

  const editRoom = (room) => {
    setRoomErrors([]);
    setRoom(room);
    onOpen();
  };

  // const deleteRoom = (e, room) => {
  //   e.target.setAttribute("disabled", "");
  //   const container = e.target.closest("div.room");
  //   container.classList.add("animate-pulse");
  //   AtriumApi.delete("/v1/rooms/" + room.id).then(() => {
  //     let collection = [...rooms];
  //     const index = collection.findIndex((r) => r.id === room.id);
  //     collection = collection
  //       .slice(0, index)
  //       .concat(collection.slice(index + 1));
  //     container.classList.remove("animate-pulse");
  //     setRooms(collection);
  //   });
  // };

  const deleteRoom = (room) => {
    setConfirmationDialog({
      open: true,
      message: "You want to delete this room?",
      onConfirm: async () => {
        try {
          await AtriumApi.delete(`/v1/rooms/${room?.id}`).then(() => {
            toast.success("Room deleted successfully.");
            onClose();
            getChurchDetails();
          });
        } catch (err) {
          toast.error(err?.data?.message || "An error occurred");
        } finally {
          setConfirmationDialog({
            open: false,
            message: "",
            onConfirm: () => {},
          });
        }
      },
    });
  };

  const handleDeleteStaff = (id) => {
    setConfirmationDialog({
      open: true,
      message: "You want to delete this staff?",
      onConfirm: async () => {
        try {
          await AtriumApi.delete(`/v1/deleteUser/${id}`).then(() => {
            toast.success("Staff deleted successfully.");
            onClose();
            getStaffList();
          });
        } catch (err) {
          toast.error(err?.data?.message || "An error occurred");
        } finally {
          setConfirmationDialog({
            open: false,
            message: "",
            onConfirm: () => {},
          });
        }
      },
    });
  };

  const getStaffList = async () => {
    setStaffListLoading(true);
    try {
      const response = await AtriumApi.get("/v1/get-staff");
      if (response) {
        setStaffList(response?.data?.data?.rows);
      }
    } catch (error) {
      toast.error("Failed to get staff list.");
    } finally {
      setStaffListLoading(false);
    }
  };

  useEffect(() => {
    getStaffList();
  }, []);

  const gradesObject = {
    0: "N/A",
    1: "Preschool",
    2: "Pre-kindergarten",
    3: "Kindergarten",
    4: "1st Grade",
    5: "2nd Grade",
    6: "3rd Grade",
    7: "4th Grade",
    8: "5th Grade",
    9: "6th Grade",
    10: "7th Grade",
    11: "8th Grade",
  };

  useEffect(() => {
    getTimeZone();
  }, []);

  const getTimeZone = async () => {
    try {
      setIsTimeZoneLoading(true);
      const response = await AtriumApi.get(`/v1/users/get-profile`);
      if (response) {
        setIsTimeZoneLoading(false);
        setTimezone(response?.data?.user?.timeZone);
      }
    } catch (error) {
      setIsTimeZoneLoading(false);
      console.log("error while getting time zone", error);
    }
  };

  const updateTimeZone = async (timezone) => {
    try {
      setUpdateTimeLoading(true);
      const payload = {
        timeZone: timezone,
      };
      await AtriumApi.patch(`/v1/users/update/${id}`, payload);
      setUpdateTimeLoading(false);
      toast.success("Timezone updated successfully.");
      getTimeZone();
    } catch (error) {
      setUpdateTimeLoading(false);
      toast.error("Failed to update timezone.");
    }
  };

  return (
    <>
      <div className="w-full block md:flex flex-row p-3 md:p-[30px] relative items-start rounded-[15px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
        <div className="flex-1 mb-6">
          <h2 className="font-bold text-lg mb-2">Organization Details</h2>
          <p className="text-gray-700 text-sm">
            Update your organization details
          </p>
        </div>

        <Formik
          enableReinitialize
          initialValues={{
            name: church?.name || "",
            email: church?.email || "",
            phone: church?.phone || "",
            address: church?.address || "",
            city: church?.city || "",
            state: church?.state || "",
            zip: church.zip || "",
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required("Church name is required"),
            email: Yup.string()
              .email("Invalid email")
              .required("Email is required"),
            phone: Yup.string()
              .matches(
                /^[0-9\s\-()+]*$/,
                "Phone number should only contain numbers."
              )
              .required("Phone number is required"),
            address: Yup.string().required("Address is required"),
            city: Yup.string().required("City is required"),
            state: Yup.string().required("State is required"),
            zip: Yup.string().required("Zip code is required"),
          })}
          onSubmit={onSaveChurch}
        >
          {({
            errors,
            touched,
            values,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <Form
              className="flex-2"
              onSubmit={(e) => {
                e.preventDefault(); // Stop default form behavior
                handleSubmit(); // Call Formik's submit
              }}
              noValidate
            >
              {notice && <AlertPane state="success">{notice}</AlertPane>}
              {/* {Object.keys(errors).length > 0 && (
                <AlertPane state="error">
                  Sorry, we have trouble to save your changes
                </AlertPane>
              )} */}
              <InputField
                variant="organization_form"
                label="Church name"
                placeholder="Please enter your church name"
                name="name"
                type="text"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.name}
                extra="pb-4 flex flex-col"
                touched={touched.name}
              />

              <div className="flex flex-col md:flex-row gap-3 md:gap-6 pb-4">
                <InputField
                  variant="organization_form"
                  label="Email address"
                  placeholder="Please enter your email address"
                  name="email"
                  type="text"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email}
                  extra="flex-1 flex flex-col"
                  touched={touched.email}
                />
                <InputField
                  variant="organization_form"
                  label="Contact phone"
                  placeholder="Please enter your contact phone"
                  name="phone"
                  type="text"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                  extra="flex-1 flex flex-col"
                  touched={touched.phone}
                />
              </div>

              <div className="flex flex-col md:flex-row gap-3 md:gap-6 pb-4">
                <InputField
                  variant="organization_form"
                  label="Address"
                  placeholder="Please enter your address"
                  name="address"
                  type="text"
                  value={values.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.address}
                  extra="flex-1 flex flex-col"
                  touched={touched.address}
                />
                <InputField
                  variant="organization_form"
                  label="City"
                  placeholder="Please enter your city"
                  name="city"
                  type="text"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.city}
                  extra="flex-1 flex flex-col"
                  touched={touched.city}
                />
              </div>

              <div className="flex gap-3 md:gap-6 pb-4">
                <InputField
                  variant="organization_form"
                  label="State"
                  placeholder="Please enter your state"
                  name="state"
                  type="text"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.state}
                  extra="flex-1 flex flex-col"
                  touched={touched.state}
                />
                <InputField
                  variant="organization_form"
                  label="Zip code"
                  placeholder="Please enter your zip code"
                  name="zip"
                  type="text"
                  value={values.zip}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.zip}
                  extra="flex-1 flex flex-col"
                  touched={touched.zip}
                />
              </div>

              <div className="flex justify-between md:justify-normal flex-row gap-4 flex-wrap">
                <DarkButton type="submit">Save changes</DarkButton>
                {/* <TransparentButton type="button" onClick={() => qrDlg.onOpen()}>
                  Generate QR Code
                </TransparentButton> */}
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* ------------------------- Add Staff ------------------------- */}

      <div className="w-full block md:flex flex-row p-3 md:p-[30px] relative items-start rounded-[15px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
        <div className="flex-1 mb-6">
          <h2 className="font-bold text-lg mb-2">Add Staff</h2>
          <p className="text-gray-700 text-sm">
            Add Staff for scheduling and check-in
          </p>
        </div>

        {/* <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="font-bold text-xl text-indigo-900">Add Staff</h2>
          <p className="text-sm text-gray-500">Add Staff for scheduling and check-in</p>
        </div>
        <button className="bg-indigo-900 text-white px-4 py-2 rounded-full flex items-center gap-2">
          <FiPlus /> Add Staff
        </button>
      </div>

      <div className="space-y-4">
        {staffList.map((staff) => (
          <div
            key={staff.id}
            className="flex justify-between items-center p-4 bg-white rounded-md shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-4">
              <img
                src={staff.image}
                alt={staff.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <p className="text-indigo-900 font-semibold">{staff.name}</p>
                <p className="text-sm text-gray-500">{staff.status}</p>
              </div>
            </div>
            <div className="flex gap-4 text-indigo-900">
              <button>
                <FiEdit2 className="w-5 h-5" />
              </button>
              <button>
                <FiTrash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div> */}

        <div className="flex-2">
          <div className="flex justify-end mb-4">
            <DarkButton
              onClick={() => {
                setIsDrawerOpen(true);
                setStaffDetails({});
              }}
            >
              <span className="flex items-center gap-2">
                <PlusIcon />
                <span>Add Staff</span>
              </span>
            </DarkButton>
          </div>
          <div className="space-y-4">
            {staffListLoading ? (
              <div className="flex justify-center items-center py-6">
                <Loader />
              </div>
            ) : (
              <>
                {staffList?.length > 0 ? (
                  staffList?.map((staff) => (
                    <div
                      key={staff?.id}
                      className="flex justify-between items-center pb-4 shadow-sm "
                    >
                      <div className="flex items-center gap-4">
                        <Avatar user={staff} size="md" />
                        {/* <img
                          src={staff.image}
                          alt={staff.name}
                          className="w-12 h-12 rounded-full object-cover"
                        /> */}
                        <div>
                          <p className="text-indigo-900 dark:text-white font-semibold">
                            {staff?.firstName} {staff?.lastName}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {staff?.status}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-4 items-center ">
                        <button
                          onClick={() => {
                            setIsDrawerOpen(true);
                            setStaffDetails(staff);
                          }}
                        >
                          <PenEditIcon />
                        </button>
                        <button onClick={() => handleDeleteStaff(staff.id)}>
                          <DeleteIcon color="red" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex justify-center items-center pt-6">
                    <p className="text-gray-500">No staff found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* -------------------------------- Add Rooms  -------------------------------- */}

      <div className="w-full block md:flex flex-row p-3 md:p-[30px] relative items-start rounded-[15px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
        <div className="flex-1 mb-6">
          <h2 className="font-bold text-lg mb-2">Rooms</h2>
          <p className="text-gray-700 text-sm">
            Add rooms for scheduling and check-in
          </p>
        </div>

        <div className="flex-2">
          <div className="w-full overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="text-gray-500 border-b">
                  <th className="py-3 whitespace-nowrap">ROOM NAME</th>
                  <th className="py-3 text-center">CAPACITY</th>
                  <th className="py-3 text-center ">GRADE</th>
                  <th className="py-3 text-center "></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      <div className="flex justify-center items-center">
                        <Loader />
                      </div>
                    </td>
                  </tr>
                ) : rooms?.length > 0 ? (
                  rooms?.map((room) => (
                    <tr
                      key={room?.id}
                      className="border-b hover:bg-gray-50 dark:hover:bg-navy-700"
                    >
                      <td className="py-4 font-semibold text-indigo-900">
                        {room?.name}
                      </td>
                      <td className="py-4 text-center">{room?.capacity}</td>
                      <td className="py-4 text-center">
                        {/* {room?.grade ? gradesObject[room?.grade] : "--"} */}
                        {room?.grade !== undefined && room?.grade !== null
                          ? gradesObject[room?.grade]
                          : "--"}
                      </td>
                      <td className="py-4 text-center">
                        <div className="flex gap-4 text-center items-center justify-end">
                          <button onClick={() => editRoom(room)}>
                            <PenEditIcon />
                          </button>
                          <button onClick={(e) => deleteRoom(room)}>
                            <DeleteIcon color="red" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-gray-500">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-6">
            <GrayButton onClick={newRoom}>
              <span className="inline-block align-middle mr-2">
                <PlusIcon />
              </span>
              <span className="inline-block align-middle">Create room</span>
            </GrayButton>
          </div>
        </div>
      </div>

      {/* ------------------------ System Preferences ------------------------ */}

      {isTimeZoneLoading ? (
        <div className="flex justify-center items-center">
          <Loader />
        </div>
      ) : (
        <div className="w-full block md:flex flex-row p-3 md:p-[30px] relative items-start rounded-[15px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
          <div className="flex-1 mb-6">
            <h2 className="font-bold text-lg mb-2">System Preferences</h2>
            <p className="text-gray-700 text-sm">
              {/* Update your system preferences */}
              Update your time zone. Affects only your account.
            </p>
          </div>

          <div className="flex-2">
            <div className="w-full ">
              <div className="flex  gap-4">
                {/* <div className="flex-1">
                <DatePicker
                  label="Date of birth"
                  placeholder="Select your date of birth"
                  value={dob}
                  onChange={(v) => setDob(v.toLocaleDateString("en-US"))}
                />
              </div> */}
                <div className="flex-1">
                  <label
                    className="text-sm text-navy-700 dark:text-white ml-2 !mb-4"
                    htmlFor="time-zone"
                  >
                    Time zone
                  </label>
                  <Select
                    id="time-zone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  >
                    {/* <option value="EST/EDT">Eastern Time (UTC-4) </option> */}
                    <option value="America/New_York">
                      Eastern Time (UTC-5 / UTC-4 ){" "}
                    </option>
                    <option value="America/Chicago">
                      Central Time (UTC-6 / UTC-5)
                    </option>
                    <option value="America/Denver">
                      Mountain Time (UTC-7 / UTC-6)
                    </option>
                    <option value="America/Los_Angeles">
                      Pacific (UTC-8 / UTC-7)
                    </option>
                    <option value="America/Anchorage">
                      Alaska Time (UTC-9 / UTC-8)
                    </option>
                    <option value="Pacific/Honolulu">
                      Hawaii-Aleutian Time (UTC-10 / UTC-9)
                    </option>
                    <option value="America/Phoenix">
                      Arizona (most areas) (UTC-7)
                    </option>
                  </Select>
                  <div className="mt-4">
                    <DarkButton
                      size="md"
                      onClick={() => updateTimeZone(timezone)}
                      className="mt-4 px-2 py-2 bg-blue-500 text-white rounded "
                    >
                      {updateTimeLoading ? "Saving" : "Save Changes"}{" "}
                    </DarkButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* <div className="w-full overflow-x-auto">
        <div className="min-w-[500px] flex flex-col gap-4">
          <div className="flex w-full text-gray-700 font-medium text-sm pb-4 items-center">
            <p className="flex-1">ROOM NAME</p>
            <p className="w-24">CAPACITY</p>
            <p className="w-24">GRADE</p>
            <p className="w-24">Action</p>
          </div>

          {rooms.map((room, i) => (
            <div
              key={i}
              className="flex w-full text-gray-700 text-sm border-b border-gray-100 dark:border-gray-700 pb-43items-center"
            >
              <p className="flex-1">{room.name}</p>
              <p className="w-24">{room.capacity}</p>
              <p className="w-24">{room.grade}</p>
              <div className="w-24 flex gap-2">
                <button className="p-2" onClick={() => editRoom(room)}>
                  <PenEditIcon />
                </button>
                <button className="p-2" onClick={(e) => deleteRoom(e, room)}>
                  <TrashIcon className="text-red-500" />
                </button>
              </div>
            </div>
          ))}

        </div>
      </div> */}
      {/* <div>
        <GrayButton onClick={newRoom}>
          <span className="inline-block align-middle mr-2">
            <PlusIcon />
          </span>
          <span className="inline-block align-middle">Create room</span>
        </GrayButton>
      </div> */}

      {/* <div className="w-full block md:flex flex-row p-3 md:p-[30px] relative items-start rounded-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
            <div className="flex-1 mb-6">
                <h2 className="font-bold text-lg mb-2">System Preferences</h2>
                <p className="text-gray-700 text-sm">Update your system preferences</p>`
            </div>

            <form onSubmit={onSavePreferences} className="flex-2">
                {preferencesNotice && <AlertPane state="success">{preferencesNotice}</AlertPane>}
                <div className="flex flex-row gap-6 text-gray-700 font-medium text-sm pb-4 items-center">
                    <div className="flex-1 flex flex-col">
                        <label className="text-sm text-navy-700 dark:text-white ml-2 mb-2" htmlFor="date-format">Date format</label>
                        <Select id="date-format" value={church.dateFormat} onChange={(e) => setChurch({...church, dateFormat: e.target.value})}>
                            <option value="1">dd/mm/yyyy</option>
                        </Select>
                    </div>

                    <div className="flex-1 flex flex-col">
                        <label className="text-sm text-navy-700 dark:text-white ml-2 mb-2" htmlFor="time-zone">Time zone</label>
                        <Select id="time-zone" value={church.timezone} onChange={(e) => setChurch({...church, timezone: e.target.value})}>
                            <option value="2">UTC+2</option>
                            <option value="-1">UTC-1</option>
                        </Select>
                    </div>
                </div>

                <DarkButton type="submit">Save changes</DarkButton>
            </form>
        </div> */}

      {/* QRCode */}
      <Dialog isOpen={qrDlg.isOpen} onClose={qrDlg.onClose} placement="top">
        <div className="flex w-full m-auto items-center content-center justify-center p-12">
          <QRCodeSVG
            value={`${window.location.protocol}//${church.host}`}
            size={240}
            bgColor={"transparent"}
            fgColor={darkmode ? "white" : "#1F264E"}
          />
        </div>
        <div className="text-center">{`${window.location.protocol}//${church.host}`}</div>
      </Dialog>

      {/* room modal */}
      <Dialog isOpen={isOpen} onClose={onClose} placement="top">
        <Formik
          enableReinitialize
          initialValues={room}
          validationSchema={Yup.object().shape({
            name: Yup.string().required("Room name is required"),
            capacity: Yup.string()
              .matches(/^[0-9\s\-()+]*$/, "Only numbers are allowed")
              .required("Capacity number is required"),
            // grade: Yup.string().required("Grade is required"),
          })}
          onSubmit={onSaveRoom}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <Form
              onSubmit={(e) => {
                e.preventDefault(); // Stop default form behavior
                handleSubmit(); // Call Formik's submit
              }}
              noValidate
            >
              <div className="text-lg font-bold dark:text-white">
                {room.id ? "Edit" : "Create"} room
              </div>
              <InputField
                variant="room_form"
                label="Room name"
                placeholder="Room name"
                name="name"
                type="text"
                value={values.name}
                error={errors.name}
                touched={touched.name}
                onChange={handleChange}
                onBlur={handleBlur}
                extra="flex flex-col pt-4"
              />

              <InputField
                variant="room_form"
                label="Room Capacity"
                placeholder="Room Capacity"
                name="capacity"
                type="text"
                value={values.capacity}
                error={errors.capacity}
                touched={touched.capacity}
                onChange={handleChange}
                onBlur={handleBlur}
                extra="flex flex-col pt-4"
              />

              <div className="flex flex-col pt-4">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="grade"
                >
                  Grade
                </label>
                <Select
                  id="grade"
                  name="grade"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.grade || ""}
                  touched={touched.grade}
                  // error={errors.grade}
                  // style={{
                  //   borderColor: touched.grade && errors.grade ? "red" : "",
                  // }}
                >
                  <option value="" disabled hidden>
                    Select grade
                  </option>
                  <option value="0">N/A</option>
                  <option value="1">Preschool</option>
                  <option value="2">Pre-kindergarten</option>
                  <option value="3">Kindergarten</option>
                  <option value="4">1st Grade</option>
                  <option value="5">2nd Grade</option>
                  <option value="6">3rd Grade</option>
                  <option value="7">4th Grade</option>
                  <option value="8">5th Grade</option>
                  <option value="9">6th Grade</option>
                  <option value="10">7th Grade</option>
                  <option value="11">8th Grade</option>
                </Select>
                {errors.grade && touched.grade && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.grade}
                  </p>
                )}
              </div>

              <div className="flex gap-2 mt-6">
                <DarkButton type="submit" disabled={isSavingRoom}>
                  {room.id ? "Save" : "Create"} room
                </DarkButton>

                <GrayButton type="button" onClick={onClose}>
                  Cancel
                </GrayButton>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>

      {isDrawerOpen && (
        <ManageStaffDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          user={staffDetails}
          getStaffList={getStaffList}
        />
      )}
      {/* <DeleteStaff
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
      /> */}

      <ConfirmationDialog
        isOpen={confirmationDialog.open}
        message={confirmationDialog.message}
        onClose={() => setConfirmationDialog({ open: false, message: "" })}
        onConfirm={confirmationDialog.onConfirm}
      />
    </>
  );
};

export default OrganizationSettings;
